import React, { useState, useEffect } from 'react'
import { XMarkIcon, PhotoIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { CreateLedgerEntryData, railbirdLedgerApi } from '../services/railbirdLedgerApi'
import PhotoUpload from './PhotoUpload'
import toast from 'react-hot-toast'

interface CreateLedgerEntryModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function CreateLedgerEntryModal({
  isOpen,
  onClose,
  onSuccess
}: CreateLedgerEntryModalProps) {
  const { user, getAccessTokenSilently } = useAuth0()
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Get the user's display name for the scribe field
  const getUserDisplayName = () => {
    if (!user) return ''
    return (user as any).given_name || (user as any).name || (user as any).email || ''
  }

  const [formData, setFormData] = useState<CreateLedgerEntryData>({
    title: '',
    body: '',
    scribe: getUserDisplayName(),
    tags: [],
    images: []
  })
  const [tagInput, setTagInput] = useState('')

  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Update scribe field when user changes or modal opens
  useEffect(() => {
    if (isOpen && user) {
      setFormData(prev => ({
        ...prev,
        scribe: getUserDisplayName()
      }))
    }
  }, [isOpen, user])

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    }

    if (!formData.body.trim()) {
      newErrors.body = 'Adventure story is required'
    }

    if (!formData.scribe.trim()) {
      newErrors.scribe = 'Scribe name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setIsSubmitting(true)
      railbirdLedgerApi.setAuth(getAccessTokenSilently)
      await railbirdLedgerApi.createEntry(formData)
      onSuccess()
      
      // Reset form
      setFormData({
        title: '',
        body: '',
        scribe: getUserDisplayName(),
        tags: [],
        images: []
      })
      setTagInput('')
      setErrors({})
    } catch (error: any) {
      console.error('Create entry error:', error)
      toast.error(error.message || 'Failed to create adventure')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddTag = () => {
    const tag = tagInput.trim()
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }))
      setTagInput('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  const handlePhotoUploaded = (photoUrl: string) => {
    setFormData(prev => ({
      ...prev,
      images: [...(prev.images || []), photoUrl]
    }))
  }

  const handlePhotoRemoved = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== index) || []
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-4xl p-6 my-8 text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-emerald-800 dark:text-emerald-200" style={{ fontFamily: 'Georgia, serif' }}>
              Chronicle New Adventure
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Adventure Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-3 border border-emerald-300 dark:border-emerald-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                style={{ fontFamily: 'Georgia, serif' }}
                placeholder="e.g. The Great Derby Adventure, Vegas Victory, Lake House Legends"
                required
              />
              {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
            </div>

            {/* Scribe */}
            <div>
              <label className="block text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Scribe (Auto-filled)
              </label>
              <input
                type="text"
                value={formData.scribe}
                readOnly
                className="w-full px-4 py-3 border border-emerald-300 dark:border-emerald-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                style={{ fontFamily: 'Georgia, serif' }}
                placeholder="Who is chronicling this adventure?"
              />
              {errors.scribe && <p className="mt-1 text-sm text-red-600">{errors.scribe}</p>}
            </div>

            {/* Adventure Story */}
            <div>
              <label className="block text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Adventure Story *
              </label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                rows={15}
                className="w-full px-4 py-3 border border-emerald-300 dark:border-emerald-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                style={{ fontFamily: 'Georgia, serif' }}
                placeholder="Share your railbird adventure... Tell us about the journey, the people, the moments that made it memorable, the wins and losses, the laughs and the legends created along the way."
                required
              />
              <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400" style={{ fontFamily: 'Georgia, serif' }}>
                Chronicle the full adventure - the journey, the characters, the memorable moments, and the stories that deserve to be remembered.
              </p>
              {errors.body && <p className="mt-1 text-sm text-red-600">{errors.body}</p>}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Adventure Tags
              </label>
              <div className="flex space-x-2 mb-3">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1 px-3 py-2 border border-emerald-300 dark:border-emerald-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Georgia, serif' }}
                  placeholder="Add a tag (e.g. derby, vegas, lake-house, epic, legendary)"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>
              
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-200"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Photos */}
            <div>
              <label className="block text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Adventure Photos
              </label>
              
              {/* Existing Photos */}
              {formData.images && formData.images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                  {formData.images.map((image, index) => (
                    <div key={index} className="relative">
                      <img
                        src={image}
                        alt={`Adventure photo ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border-2 border-emerald-200 dark:border-emerald-700"
                      />
                      <button
                        type="button"
                        onClick={() => handlePhotoRemoved(index)}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <MinusIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Photo Upload */}
              <div className="border-2 border-dashed border-emerald-300 dark:border-emerald-600 rounded-lg p-4">
                <PhotoUpload
                  currentPhoto=""
                  onPhotoUploaded={handlePhotoUploaded}
                  onPhotoRemoved={() => {}}
                />
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2 text-center" style={{ fontFamily: 'Georgia, serif' }}>
                  Add photos from your adventure - group shots, memorable moments, scenic views, or victory celebrations
                </p>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-emerald-200 dark:border-emerald-600">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                {isSubmitting ? 'Chronicling Adventure...' : 'Chronicle Adventure'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
